import React, { useState } from 'react';
import { Chart } from '@antv/g2';
import moment from 'moment';
import PriceChartItem from '../../../types/PriceChartItem';
import UserContext from 'src/services/UserContext';
import styles from './PriceChart.module.less';

type PriceChartProps = {
  data: PriceChartItem[] | null;
  selectedCompetitors: string[];
  ownerName: string;
};

type LegendProps = {
  selectedCompetitors: string[];
  ownerName: string;
};

type LegendItemProps = {
  index: number;
  label?: string;
  name: string;
  color: string;
};

type RenderColumnChartProps = {
  data: PriceChartItem[] | null;
  id: string;
};

const COLORS = ['#FFC0CB', '#002D74', '#6BA53A', '#F8D210', '#2FF3E0'];

const renderColumnChart = ({
  id,
  data,
  colors,
}: RenderColumnChartProps & { colors: { [key: string]: string } }): void => {
  const canvasWrapper = document.getElementById(id);
  if (canvasWrapper) {
    canvasWrapper.innerHTML = '';
  }

  if (data) {
    const chart = new Chart({
      container: id,
      autoFit: true,
      height: 200,
      width: 400,
      padding: [40, 5, 20, 70],
      localRefresh: true,
      pixelRatio: window.devicePixelRatio,
      limitInPlot: true,
    });
    chart.data(data);
    chart.scale({
      day: {
        range: [0, 1],
        tickCount: 4,
      },
      value: {
        nice: true,
        minLimit: 0,
      },
    });
    chart.tooltip({
      showCrosshairs: true,
      shared: true,
      title: (value) => moment(value).format(UserContext.getDateFormat()),
    });
    chart.axis('day', {
      label: {
        style: {
          textAlign: 'end',
        },
        formatter: (value) => moment(value).format(UserContext.getDateFormat()),
      },
    });
    chart.axis('value', {
      label: {
        formatter: (value) => `$${Number(value).toFixed(2)}`,
      },
    });
    chart.legend(false);
    chart
      .line()
      .position('day*value')
      .color('type', (type) => colors[type])
      .style('isOwner', (isOwner) => {
        const style: { lineWidth: number; lineDash?: [number, number] } = { lineWidth: 3 };
        if (isOwner) {
          style.lineDash = [10, 10];
        }
        return style;
      })
      .shape('smooth');

    // TODO: get colors from back-end
    chart.theme({
      styleSheet: {
        brandColor: '#002D74',
        paletteQualitative10: Object.values(colors),
        paletteQualitative20: Object.values(colors),
      },
    });
    chart.render();
  }
};

const LegendItem = ({ label, name, color }: LegendItemProps) => {
  return (
    <span className={styles.legendItem}>
      {label && (
        <>
          <strong>
            {label}
            <span>: </span>
          </strong>
        </>
      )}
      {name} <span className={styles.marker} style={{ borderColor: color }}></span>
    </span>
  );
};

const Legend = ({ ownerName, selectedCompetitors }: LegendProps) => {
  const colors: { [key: string]: string } = {
    [ownerName]: '#f00',
    ...selectedCompetitors.reduce<{ [key: string]: string }>((acc, competitor, i) => {
      acc[competitor] = COLORS[i] || '#000';
      return acc;
    }, {}),
  };

  return (
    <div className={styles.legend}>
      <LegendItem label={'Source Site'} name={ownerName} index={0} color={colors[ownerName]} />
      {selectedCompetitors.length > 0 && <strong>Competitor Sites:</strong>}
      {selectedCompetitors.map((competitor: string, i: number) => (
        <LegendItem key={competitor} name={competitor} index={i + 1} color={colors[competitor]} />
      ))}
    </div>
  );
};

const PriceChart = ({ data, ownerName, selectedCompetitors }: PriceChartProps) => {
  const [dataLength, setDataLength] = useState(0);
  const id = 'price_history_chart';

  React.useEffect(() => {
    if (data && data.length !== dataLength) {
      setDataLength(data.length);
      const colors: { [key: string]: string } = {
        [ownerName]: '#f00',
        ...selectedCompetitors.reduce<{ [key: string]: string }>((acc, competitor, i) => {
          acc[competitor] = COLORS[i] || '#000';
          return acc;
        }, {}),
      };
      renderColumnChart({ id, data, colors });
    }
  }, [data, dataLength]);

  return (
    <>
      <Legend ownerName={ownerName} selectedCompetitors={selectedCompetitors} />
      <div id={id} />
    </>
  );
};

export default PriceChart;
